import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { BaseDialog } from './BaseDialog';

export interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  itemName?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  showDeleteIcon?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  message,
  confirmLabel,
  cancelLabel,
  loading = false,
  showDeleteIcon = false,
  maxWidth = 'xs',
}) => {
  const { t } = useTranslation();
  const isBusy = loading;

  const dialogTitle = title || t('common.confirmDelete', 'Confirm Delete');
  const dialogMessage = message || (
    itemName
      ? t('common.confirmDeleteItem', 'Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: itemName })
      : t('common.confirmDeleteGeneric', 'Are you sure you want to delete this item? This action cannot be undone.')
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      maxWidth={maxWidth}
      loading={isBusy}
      actions={(
        <>
          <Button onClick={onClose} disabled={isBusy}>
            {cancelLabel || t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            color="error"
            variant="contained"
            disabled={isBusy}
            startIcon={showDeleteIcon ? <DeleteIcon /> : undefined}
          >
            {isBusy
              ? t('common.deleting', 'Deleting...')
              : (confirmLabel || t('common.delete', 'Delete'))}
          </Button>
        </>
      )}
    >
      <Typography>{dialogMessage}</Typography>
    </BaseDialog>
  );
};

export default DeleteConfirmationDialog;
